import { useEffect, useState } from 'react';

import { Label } from 'cpool-components/general/Label';
import { CurrencyInput } from 'cpool-components/input/CurrencyInput';
import { DropdownMenu } from 'cpool-components/input/DropdownMenu';
import { PercentInput } from 'cpool-components/input/PercentInput';
import { EUROPACE_PAYMENT_DATES, LOAN_REQUEST_USAGES } from 'cpool-constants';
import { useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { LuPencil } from 'react-icons/lu';

import { BankCreditRequestFormSection } from '../BankCreditRequestFormSection';
import { CarFinanceForms } from './CarFinance/CarFinanceForms';
import { RequestConfigHeaderNotifications } from './RequestConfigHeaderNotifications';
import type { SchemaCreditRequestForm } from '../schemas/creditRequestForm.schema';
import type { getRuntimeOptions } from './util/getRuntimeOptions';

export const RequestConfiguration = ({ runtimeOptions }: { runtimeOptions: ReturnType<typeof getRuntimeOptions> }) => {
    const { t } = useTranslation();
    const methods = useFormContext<SchemaCreditRequestForm>();

    // NOTE: unfortunately we need to register the fields here, because the ref is not usable in our input components (except the TextInput component)
    // also the onChange returns a single value (except for the TextInput component) so we cannot pass the value to the onChange function as is
    const registerVerwendungszweck = methods.register('verwendungszweck');
    const registerRatenZahlungstermin = methods.register('ratenzahlungstermin');
    const registerKreditLaufzeit = methods.register('kreditLaufzeit');
    const registerKreditBetrag = methods.register('kreditBetrag');
    const registerProvision = methods.register('provision');

    // NOTE: as mentioned above we need to watch the values here since we cannot use the ref in some input components
    const valueVerwendungszweck = useWatch({ name: 'verwendungszweck' });
    const valueRatenZahlungstermin = useWatch({ name: 'ratenzahlungstermin' });
    const valueKreditLaufzeit = useWatch({ name: 'kreditLaufzeit' });
    const valueKreditBetrag = useWatch({ name: 'kreditBetrag' });
    const valueProvision = useWatch({ name: 'provision' });

    const [showCarFinanceForms, setShowCarFinanceForms] = useState<boolean>(false);

    useEffect(() => {
        if (valueVerwendungszweck === LOAN_REQUEST_USAGES.FAHRZEUGKAUF) {
            setShowCarFinanceForms(true);
        } else {
            setShowCarFinanceForms(false);
        }
    }, [valueVerwendungszweck]);

    return (
        <BankCreditRequestFormSection
            testId="request-configuration"
            title={t('BANK_CREDIT_REQUEST.REQUEST_CONFIGURATION.TITLE')}
            icon={<LuPencil className="w-[15px] h-[15px]" />}
            headerNotification={<RequestConfigHeaderNotifications />}
        >
            <div className="flex flex-col gap-2 border-t border-grey-light pt-3">
                <div className="text-sm text-midnight font-bold py-1.5">
                    {t('BANK_CREDIT_REQUEST.REQUEST_CONFIGURATION.CREDIT_REQUEST.TITLE')}
                </div>
                <div className="flex gap-1">
                    <Label htmlFor="kreditBetrag" className="w-[172px] justify-start">
                        {t('BANK_CREDIT_REQUEST.REQUEST_CONFIGURATION.CREDIT_REQUEST.FORM_INPUT_LABELS.CREDIT_AMOUNT')}
                    </Label>
                    <CurrencyInput
                        id="kreditBetrag"
                        name="kreditBetrag"
                        className="w-full"
                        onChange={(value) => {
                            registerKreditBetrag.onChange({ target: { name: 'kreditBetrag', value } });
                        }}
                        value={valueKreditBetrag}
                    />
                </div>
                <div className="flex gap-1 justify-between">
                    <Label htmlFor="kreditLaufzeit" className="w-[172px] justify-start">
                        {t('BANK_CREDIT_REQUEST.REQUEST_CONFIGURATION.CREDIT_REQUEST.FORM_INPUT_LABELS.RUNTIME')}
                    </Label>
                    <DropdownMenu
                        id="kreditLaufzeit"
                        className="w-full"
                        options={runtimeOptions}
                        onChange={(value) => {
                            registerKreditLaufzeit.onChange({ target: { name: 'kreditLaufzeit', value } });
                        }}
                        value={valueKreditLaufzeit}
                        triggerClassName="w-full"
                    />
                </div>
                <div className="flex gap-1 justify-between">
                    <Label htmlFor="verwendungszweck" className="w-[172px] justify-start">
                        {t(
                            'BANK_CREDIT_REQUEST.REQUEST_CONFIGURATION.CREDIT_REQUEST.FORM_INPUT_LABELS.FINANCING_PURPOSE',
                        )}
                    </Label>
                    <DropdownMenu
                        id="verwendungszweck"
                        options={Object.values(LOAN_REQUEST_USAGES).map((value) => ({
                            label: t(`FUNDING_TYPES.${value}`),
                            value,
                        }))}
                        value={valueVerwendungszweck}
                        className="w-full"
                        onChange={(value) => {
                            registerVerwendungszweck.onChange({ target: { name: 'verwendungszweck', value } });
                        }}
                        triggerClassName="w-full"
                    ></DropdownMenu>
                </div>
                <div className="flex gap-1 justify-between">
                    <Label htmlFor="provision" className="w-[172px] justify-start">{`${t(
                        'BANK_CREDIT_REQUEST.REQUEST_CONFIGURATION.CREDIT_REQUEST.FORM_INPUT_LABELS.COMMISSION',
                    )}`}</Label>
                    <PercentInput
                        id="provision"
                        className="w-full"
                        onChange={(value) => {
                            registerProvision.onChange({ target: { name: 'provision', value } });
                        }}
                        value={valueProvision}
                    />
                </div>
                <div className="flex gap-1 justify-between">
                    <Label htmlFor="ratenEinzug" className="w-[172px] justify-start">{`${t(
                        'BANK_CREDIT_REQUEST.REQUEST_CONFIGURATION.CREDIT_REQUEST.FORM_INPUT_LABELS.INSTALLMENT_PAYMENT_DATE',
                    )}`}</Label>
                    <DropdownMenu
                        id="ratenEinzug"
                        options={Object.values(EUROPACE_PAYMENT_DATES).map((value) => ({
                            label: t(`BANK_CREDIT_REQUEST.PAYMENT_DATES.${value}`),
                            value,
                        }))}
                        className="w-full"
                        onChange={(value) => {
                            registerRatenZahlungstermin.onChange({ target: { name: 'ratenzahlungstermin', value } });
                        }}
                        value={valueRatenZahlungstermin}
                        triggerClassName="w-full"
                    ></DropdownMenu>
                </div>
            </div>
            <CarFinanceForms isOpen={showCarFinanceForms} />
        </BankCreditRequestFormSection>
    );
};
